import { createSignal } from 'solid-js'
import {
   applyPausedState,
   approximateScheduleDays,
   getCalendarNextDueAt,
   getHabitLastResumedAt,
   habitHasTag,
   isCalendarHabitOverdue,
   normalizeHabitTags,
   normalizePausePeriods,
   normalizeScheduleRule,
   resolveHabitCompletionChain,
   serializePausePeriods,
   type HabitPausePeriod,
} from '@fasting-timer/shared'

import { dispatchHabitStateChange } from '../runtimeBridge'

export type Habit = {
   id: string
   name: string
   /**
    * `recurring`: due `scheduleDays` after the last completion. `one-off`: due on
    * `dueDate`. `calendar`: due on the dates `scheduleRule` (an RRULE) produces,
    * starting at `dueDate`, whose local time is the time of day.
    */
   kind: 'recurring' | 'one-off' | 'calendar'
   scheduleDays: number
   dueDate: number | null
   scheduleRule: string | null
   paused: boolean
   /** When the habit was paused, oldest first; paused days don't count against it. */
   pausePeriods: HabitPausePeriod[]
   lastCompletedAt: number | null
   completionHistory: number[]
   alsoCompletes: string[]
   tags: string[]
   createdAt: number
   updatedAt: number
}

type HabitAction =
   | 'add-habit'
   | 'update-habit'
   | 'delete-habit'
   | 'complete-habit'
   | 'toggle-habit-paused'
   | 'set-tag-paused'
   | 'sync-habits'

const HABITS_STORAGE_KEY = 'habits'
const DELETED_HABITS_STORAGE_KEY = 'deletedHabits'
const DAY_MS = 24 * 60 * 60 * 1000
const HOUR_MS = 60 * 60 * 1000
const TOMBSTONE_RETENTION_MS = 90 * DAY_MS
export const HABIT_COMPLETION_ANIMATION_MS = 1000
const DEFAULT_DUE_TIME = '00:00'
const DEFAULT_SCHEDULE_RULE = 'FREQ=WEEKLY'

const [habits, setHabitsSignal] = createSignal<Habit[]>([])
const [habitNameInput, setHabitNameInput] = createSignal('')
const [habitScheduleDaysInput, setHabitScheduleDaysInput] = createSignal('1')
const [habitKindInput, setHabitKindInput] = createSignal<Habit['kind']>('recurring')
const [habitDueDateInput, setHabitDueDateInput] = createSignal(getLocalDateInputValue())
const [habitDueTimeInput, setHabitDueTimeInput] = createSignal(DEFAULT_DUE_TIME)
const [habitScheduleRuleInput, setHabitScheduleRuleInput] =
   createSignal(DEFAULT_SCHEDULE_RULE)
const [habitAlsoCompletesInput, setHabitAlsoCompletesInput] = createSignal<string[]>([])
const [habitTagsInput, setHabitTagsInput] = createSignal<string[]>([])
const [celebratingHabitIds, setCelebratingHabitIds] = createSignal<string[]>([])
let deletedHabitTombstones = new Map<string, number>()

/**
 * Parses a number or date string into epoch milliseconds.
 *
 * @param value - The candidate value.
 * @returns Epoch milliseconds, or null.
 */
function parseTimestamp(value: unknown): number | null {
   if (typeof value === 'number' && Number.isFinite(value)) {
      return value
   }
   if (typeof value === 'string' && value) {
      const parsed = Date.parse(value)
      return Number.isFinite(parsed) ? parsed : null
   }
   return null
}

/**
 * Generates a unique habit id (crypto UUID, or a random fallback).
 *
 * @returns A new habit id.
 * @throws Error when secure randomness is unavailable.
 */
function createHabitId() {
   if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
   }
   if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const bytes = crypto.getRandomValues(new Uint32Array(2))
      return `habit-${Date.now()}-${Array.from(bytes, value => value.toString(36)).join('')}`
   }
   throw new Error('Secure random number generation is unavailable.')
}

/**
 * Clamps a schedule-days value to the 1-365 range.
 *
 * @param value - The candidate value.
 * @returns The clamped whole number of days.
 */
function normalizeScheduleDays(value: unknown) {
   const numeric = typeof value === 'number' ? value : Number(value)
   return Number.isFinite(numeric) ? Math.max(1, Math.min(365, Math.round(numeric))) : 1
}

/**
 * Normalizes a habit kind to `recurring` or `one-off`.
 *
 * @param value - The candidate kind.
 * @returns The normalized habit kind.
 */
function normalizeHabitKind(value: unknown): Habit['kind'] {
   return value === 'one-off' || value === 'one_off' ? 'one-off' : 'recurring'
}

/**
 * Normalizes an "also completes" habit-id list, dropping non-strings, duplicates, and the habit's own id.
 *
 * @param value - The candidate list.
 * @param selfId - The owning habit's id.
 * @returns The normalized habit-id list.
 */
function normalizeAlsoCompletes(value: unknown, selfId: string) {
   if (!Array.isArray(value)) {
      return []
   }
   return Array.from(
      new Set(
         value.filter(
            (id): id is string => typeof id === 'string' && id !== '' && id !== selfId,
         ),
      ),
   )
}

/**
 * Formats a timestamp as a local `YYYY-MM-DD` key.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @returns The local date key.
 */
function formatLocalDateKey(timestamp: number) {
   const date = new Date(timestamp)
   return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate(),
   ).padStart(2, '0')}`
}

/**
 * Returns a local date-input value (`YYYY-MM-DD`) for a timestamp.
 *
 * @param timestamp - The epoch-ms timestamp (defaults to now).
 * @returns The date-input string.
 */
function getLocalDateInputValue(timestamp = Date.now()) {
   return formatLocalDateKey(timestamp)
}

/**
 * Parses a `YYYY-MM-DD` string into the local-midnight timestamp.
 *
 * @param value - The candidate date string.
 * @returns Epoch ms at local midnight, or null when invalid.
 */
function parseLocalDateInput(value: unknown): number | null {
   if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return null
   }
   const parts = value.split('-').map(Number)
   const [year, month, day] = parts
   if (
      parts.length !== 3 ||
      year === undefined ||
      month === undefined ||
      day === undefined
   ) {
      return null
   }
   const date = new Date(year, month - 1, day)
   date.setHours(0, 0, 0, 0)
   return Number.isFinite(date.getTime()) ? date.getTime() : null
}

/**
 * Combines a `YYYY-MM-DD` date and an optional `HH:MM` time into a local timestamp.
 *
 * @param dateValue - The date-input value.
 * @param timeValue - The time-input value (midnight when empty or invalid).
 * @returns Epoch ms, or null when the date is invalid.
 */
function parseLocalDateTimeInput(dateValue: unknown, timeValue: unknown): number | null {
   const dayStart = parseLocalDateInput(dateValue)
   if (dayStart === null) {
      return null
   }
   const match =
      typeof timeValue === 'string' ? /^(\d{1,2}):(\d{2})$/.exec(timeValue) : null
   if (!match) {
      return dayStart
   }
   const date = new Date(dayStart)
   date.setHours(Math.min(23, Number(match[1])), Math.min(59, Number(match[2])), 0, 0)
   return date.getTime()
}

/**
 * Returns a local time-input value (`HH:MM`) for a timestamp.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @returns The time-input string.
 */
export function getLocalTimeInputValue(timestamp: number) {
   const date = new Date(timestamp)
   return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

/**
 * Parses a habit due date from a local date string or timestamp.
 *
 * @param value - The candidate value.
 * @returns Epoch milliseconds, or null.
 */
function parseDueDate(value: unknown): number | null {
   return parseLocalDateInput(value) ?? parseTimestamp(value)
}

/**
 * Loads deleted-habit tombstones from storage, dropping ones past the retention window.
 */
function loadDeletedHabitTombstones() {
   if (typeof window === 'undefined') {
      return
   }

   try {
      const parsed = JSON.parse(
         window.localStorage.getItem(DELETED_HABITS_STORAGE_KEY) ?? '{}',
      ) as unknown
      const record = parsed && typeof parsed === 'object' ? parsed : {}
      const cutoff = Date.now() - TOMBSTONE_RETENTION_MS
      deletedHabitTombstones = new Map(
         Object.entries(record as Record<string, unknown>)
            .map(([id, value]) => {
               const timestamp = parseTimestamp(value)
               return timestamp === null ? null : ([id, timestamp] as const)
            })
            .filter(
               (entry): entry is readonly [string, number] =>
                  entry !== null && entry[1] >= cutoff,
            ),
      )
   } catch (error) {
      console.error('Failed to parse deleted habits from storage', error)
      deletedHabitTombstones = new Map()
   }
}

/**
 * Persists the deleted-habit tombstones to storage.
 */
function persistDeletedHabitTombstones() {
   if (typeof window === 'undefined') {
      return
   }
   window.localStorage.setItem(
      DELETED_HABITS_STORAGE_KEY,
      JSON.stringify(Object.fromEntries(deletedHabitTombstones.entries())),
   )
}

/**
 * Merges remote deletion tombstones into local state, keeping the newest per habit.
 *
 * @param entries - A map of habit id to deletion timestamp.
 */
function mergeDeletedHabitTombstonesFromSync(entries: unknown) {
   loadDeletedHabitTombstones()
   if (!entries || typeof entries !== 'object') {
      return
   }

   let changed = false
   for (const [id, value] of Object.entries(entries as Record<string, unknown>)) {
      const deletedAt = parseTimestamp(value)
      if (deletedAt === null) {
         continue
      }
      const currentDeletedAt = deletedHabitTombstones.get(id) ?? 0
      if (deletedAt > currentDeletedAt) {
         deletedHabitTombstones.set(id, deletedAt)
         changed = true
      }
   }

   if (changed) {
      persistDeletedHabitTombstones()
   }
}

/**
 * Returns a habit's freshness timestamp (latest of updated/completed/created).
 *
 * @param habit - The habit.
 * @returns The freshness timestamp in epoch ms.
 */
function getHabitFreshness(habit: Habit) {
   return Math.max(habit.updatedAt, habit.lastCompletedAt ?? 0, habit.createdAt)
}

/**
 * Returns whether a habit is hidden by a newer deletion tombstone.
 *
 * @param habit - The habit.
 * @returns True when suppressed.
 */
function isHabitSuppressedByTombstone(habit: Habit) {
   const deletedAt = deletedHabitTombstones.get(habit.id)
   return typeof deletedAt === 'number' && deletedAt >= getHabitFreshness(habit)
}

/**
 * Normalizes raw habit entries and drops those suppressed by tombstones.
 *
 * @param entries - The raw entries.
 * @returns The normalized, visible habits.
 */
export function normalizeHabitsForStorage(entries: unknown): Habit[] {
   loadDeletedHabitTombstones()
   return Array.isArray(entries) ?
         entries
            .map(normalizeHabit)
            .filter((entry): entry is Habit => entry !== null)
            .filter(entry => !isHabitSuppressedByTombstone(entry))
      :  []
}

/**
 * Normalizes a habit's schedule fields: kind, interval, RRULE, and due date/anchor.
 *
 * @param record - The raw habit record.
 * @param createdAt - The habit's creation time (a calendar habit's fallback anchor).
 * @param now - The reference time (a one-off habit's fallback due date).
 * @returns The schedule fields of a {@link Habit}.
 */
function normalizeHabitSchedule(
   record: Record<string, unknown>,
   createdAt: number,
   now: number,
): Pick<Habit, 'kind' | 'scheduleDays' | 'dueDate' | 'scheduleRule'> {
   const scheduleRule = normalizeScheduleRule(record.scheduleRule ?? record.schedule_rule)
   const rawKind = record.kind ?? record.type ?? record.taskType
   const rawDueDate = record.dueDate ?? record.due_date
   if (rawKind === 'calendar' && scheduleRule) {
      return {
         kind: 'calendar',
         scheduleDays: approximateScheduleDays(scheduleRule),
         dueDate: parseTimestamp(rawDueDate) ?? createdAt,
         scheduleRule,
      }
   }
   const kind = normalizeHabitKind(rawKind)
   return {
      kind,
      scheduleDays: normalizeScheduleDays(record.scheduleDays ?? record.schedule_days),
      dueDate:
         kind === 'one-off' ? (parseDueDate(rawDueDate) ?? getLocalDayStart(now)) : null,
      scheduleRule: null,
   }
}

/**
 * Normalizes a single habit from camelCase/snake_case input, deriving completion history and due date.
 *
 * @param entry - The raw habit record.
 * @returns The normalized habit, or null when invalid.
 */
export function normalizeHabit(entry: unknown): Habit | null {
   if (!entry || typeof entry !== 'object') {
      return null
   }

   const record = entry as Record<string, unknown>
   let id: string
   if (typeof record.id === 'string' && record.id) {
      id = record.id
   } else if (typeof record.habitId === 'string' && record.habitId) {
      id = record.habitId
   } else if (typeof record.habit_id === 'string' && record.habit_id) {
      id = record.habit_id
   } else {
      id = createHabitId()
   }
   const name = typeof record.name === 'string' ? record.name.trim() : ''
   if (!name) {
      return null
   }

   let rawCompletionHistory: unknown[] = []
   if (Array.isArray(record.completionHistory)) {
      rawCompletionHistory = record.completionHistory
   } else if (Array.isArray(record.completion_history)) {
      rawCompletionHistory = record.completion_history
   }
   const completionHistory = rawCompletionHistory
      .map(parseTimestamp)
      .filter((value): value is number => value !== null)
      .sort((a, b) => a - b)
   const lastCompletedAt =
      parseTimestamp(record.lastCompletedAt) ??
      parseTimestamp(record.last_completed_at) ??
      completionHistory[completionHistory.length - 1] ??
      null
   const now = Date.now()
   const createdAt =
      parseTimestamp(record.createdAt) ?? parseTimestamp(record.created) ?? now

   return {
      id,
      name,
      ...normalizeHabitSchedule(record, createdAt, now),
      paused: record.paused === true || record.paused === 1,
      pausePeriods: normalizePausePeriods(record.pausePeriods ?? record.pause_periods),
      lastCompletedAt,
      completionHistory: Array.from(new Set(completionHistory)),
      alsoCompletes: normalizeAlsoCompletes(
         record.alsoCompletes ?? record.also_completes,
         id,
      ),
      tags: normalizeHabitTags(record.tags),
      createdAt,
      updatedAt:
         parseTimestamp(record.updatedAt) ?? parseTimestamp(record.updated_at) ?? now,
   }
}

/**
 * Serializes a habit to its persisted/sync JSON form with ISO timestamps.
 *
 * @param habit - The habit.
 * @returns The serializable habit record.
 */
export function serializeHabit(habit: Habit) {
   return {
      id: habit.id,
      name: habit.name,
      kind: habit.kind,
      scheduleDays: habit.scheduleDays,
      dueDate: habit.dueDate === null ? null : new Date(habit.dueDate).toISOString(),
      // Always sent ('' when not a calendar habit) so the server can tell this
      // client apart from older ones that don't know the field.
      scheduleRule: habit.scheduleRule ?? '',
      // Derived, for the native badge scripts that can't evaluate RRULEs; it only
      // changes when the habit is completed or edited, so the stored copy stays valid.
      nextDueAt:
         habit.kind === 'calendar' ? toIsoOrNull(getHabitNextDueAt(habit)) : undefined,
      paused: habit.paused,
      pausePeriods: serializePausePeriods(habit.pausePeriods),
      lastCompletedAt:
         habit.lastCompletedAt === null ?
            null
         :  new Date(habit.lastCompletedAt).toISOString(),
      completionHistory: habit.completionHistory.map(timestamp =>
         new Date(timestamp).toISOString(),
      ),
      alsoCompletes: habit.alsoCompletes,
      tags: habit.tags,
      createdAt: new Date(habit.createdAt).toISOString(),
      updatedAt: new Date(habit.updatedAt).toISOString(),
   }
}

/**
 * Formats a timestamp as ISO, or null when it isn't finite.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @returns The ISO string, or null.
 */
function toIsoOrNull(timestamp: number) {
   return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : null
}

/**
 * Sorts habits by next-due time, then creation time.
 *
 * @param nextHabits - The habits to sort.
 * @returns A new sorted array.
 */
function sortHabits(nextHabits: Habit[]) {
   // Calendar due dates walk the rule's occurrences, so compute each once.
   const dueById = new Map(nextHabits.map(habit => [habit.id, getHabitNextDueAt(habit)]))
   return [...nextHabits].sort((a, b) => {
      const aDue = dueById.get(a.id) ?? 0
      const bDue = dueById.get(b.id) ?? 0
      if (aDue !== bDue) {
         return aDue - bDue
      }
      return a.createdAt - b.createdAt
   })
}

/**
 * Persists the current habits to storage.
 */
function persistHabits() {
   if (typeof window === 'undefined') {
      return
   }
   window.localStorage.setItem(
      HABITS_STORAGE_KEY,
      JSON.stringify(habits().map(serializeHabit)),
   )
}

/**
 * Sorts, stores, and broadcasts a new habits list.
 *
 * @param nextHabits - The updated habits.
 * @param action - The action that produced the change.
 */
function commitHabits(nextHabits: Habit[], action: HabitAction) {
   const normalized = sortHabits(nextHabits)
   setHabitsSignal(normalized)
   persistHabits()
   dispatchHabitStateChange({
      action,
      habits: normalized.map(serializeHabit),
   })
}

/**
 * Loads habits from storage into the store on startup.
 */
export function hydrateHabitsFromStorage() {
   if (typeof window === 'undefined') {
      return
   }
   loadDeletedHabitTombstones()
   const saved = window.localStorage.getItem(HABITS_STORAGE_KEY)
   if (!saved) {
      return
   }
   try {
      const parsed = JSON.parse(saved) as unknown
      applyHabitsFromLegacy(parsed)
   } catch (error) {
      console.error('Failed to parse habits from storage', error)
   }
}

/**
 * Adopts habit changes persisted by other same-origin tabs/windows.
 *
 * The writer already merged and persisted, so the stored list is adopted
 * as-is without writing back — persisting here would re-fire storage events
 * in other contexts and ping-pong.
 *
 * @returns A cleanup function that removes the storage listener.
 */
export function subscribeHabitsToStorageChanges() {
   if (typeof window === 'undefined') {
      return () => {}
   }

   /**
    * Applies a cross-tab habits storage change to the local store.
    *
    * @param event - The storage event.
    */
   const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== HABITS_STORAGE_KEY || typeof event.newValue !== 'string') {
         return
      }
      try {
         const parsed = JSON.parse(event.newValue) as unknown
         setHabitsSignal(sortHabits(normalizeHabitsForStorage(parsed)))
      } catch (error) {
         console.error('Failed to apply habits from another tab', error)
      }
   }

   window.addEventListener('storage', handleStorageChange)
   return () => window.removeEventListener('storage', handleStorageChange)
}

/**
 * Merges incoming habits with local ones (newest `updatedAt` wins) and persists.
 *
 * @param entries - The incoming habit entries.
 */
export function applyHabitsFromLegacy(entries: unknown) {
   const incoming = normalizeHabitsForStorage(entries)
   const mergedById = new Map<string, Habit>()
   for (const entry of incoming) {
      mergedById.set(entry.id, entry)
   }
   for (const localEntry of habits()) {
      const incomingEntry = mergedById.get(localEntry.id)
      if (!incomingEntry || localEntry.updatedAt >= incomingEntry.updatedAt) {
         mergedById.set(localEntry.id, localEntry)
      }
   }
   setHabitsSignal(sortHabits(Array.from(mergedById.values())))
   persistHabits()
}

/**
 * Merges a remote habit's completion data with the local copy so a sync echo
 * can never erase completions this device has already recorded.
 *
 * @param remoteHabit - The normalized remote habit.
 * @param localHabit - The local habit with the same id, if any.
 * @returns The remote habit with unioned completion history.
 */
function mergeHabitCompletionData(remoteHabit: Habit, localHabit: Habit | undefined) {
   if (!localHabit) {
      return remoteHabit
   }
   const completionHistory = Array.from(
      new Set([...localHabit.completionHistory, ...remoteHabit.completionHistory]),
   ).sort((a, b) => a - b)
   const lastCompletedAt = Math.max(
      localHabit.lastCompletedAt ?? Number.NEGATIVE_INFINITY,
      remoteHabit.lastCompletedAt ?? Number.NEGATIVE_INFINITY,
      completionHistory[completionHistory.length - 1] ?? Number.NEGATIVE_INFINITY,
   )
   return {
      ...remoteHabit,
      completionHistory,
      lastCompletedAt: Number.isFinite(lastCompletedAt) ? lastCompletedAt : null,
   }
}

/**
 * Replaces all habits with a normalized remote set, unioning each habit's
 * completion history with local data, and persists.
 *
 * @param entries - The remote habit entries.
 * @returns The merged habits that were stored.
 */
export function replaceHabitsFromRemote(entries: unknown) {
   const localById = new Map(habits().map(habit => [habit.id, habit]))
   const incoming = normalizeHabitsForStorage(entries).map(entry =>
      mergeHabitCompletionData(entry, localById.get(entry.id)),
   )
   const merged = sortHabits(incoming)
   setHabitsSignal(merged)
   persistHabits()
   return merged
}

/**
 * Replaces the habits with a list merged by the habits delta sync, recording
 * habits deleted elsewhere as local tombstones. No change event is dispatched:
 * the sync already holds this state, so re-queuing it would loop.
 *
 * @param nextHabits - The merged habits.
 * @param deleted - Deletion times (epoch ms) by habit id.
 * @returns The stored habits.
 */
export function setHabitsFromSync(nextHabits: Habit[], deleted: Record<string, number>) {
   mergeDeletedHabitTombstonesFromSync(deleted)
   const sorted = sortHabits(nextHabits)
   setHabitsSignal(sorted)
   persistHabits()
   return sorted
}

/**
 * Adds a habit from the current input signals.
 *
 * @returns True when added, false when the name is empty.
 */
export function addHabit() {
   const name = habitNameInput().trim()
   if (!name) {
      return false
   }
   const now = Date.now()
   const schedule = resolveScheduleInputs({
      kind: habitKindInput(),
      scheduleDays: habitScheduleDaysInput(),
      scheduleRule: habitScheduleRuleInput(),
      dueDate: habitDueDateInput(),
      dueTime: habitDueTimeInput(),
      now,
   })
   if (!schedule) {
      return false
   }
   const { kind } = schedule
   commitHabits(
      [
         ...habits(),
         {
            id: createHabitId(),
            name,
            ...schedule,
            paused: false,
            pausePeriods: [],
            lastCompletedAt: null,
            completionHistory: [],
            alsoCompletes:
               kind === 'recurring' ?
                  normalizeAlsoCompletes(habitAlsoCompletesInput(), '')
               :  [],
            tags: normalizeHabitTags(habitTagsInput()),
            createdAt: now,
            updatedAt: now,
         },
      ],
      'add-habit',
   )
   setHabitNameInput('')
   setHabitScheduleDaysInput('1')
   setHabitKindInput('recurring')
   setHabitDueDateInput(getLocalDateInputValue())
   setHabitDueTimeInput(DEFAULT_DUE_TIME)
   setHabitScheduleRuleInput(DEFAULT_SCHEDULE_RULE)
   setHabitAlsoCompletesInput([])
   setHabitTagsInput([])
   return true
}

/** The habit form's schedule fields, as its inputs hold them. */
type ScheduleInputs = {
   kind: Habit['kind']
   /** The interval field's text, e.g. `"3"`. */
   scheduleDays: string
   scheduleRule?: string
   /** A local `YYYY-MM-DD` date. */
   dueDate: string
   /** A local `HH:MM` time. */
   dueTime?: string
   now: number
}

/**
 * Turns the habit form's schedule inputs into stored habit fields.
 *
 * @param inputs - Kind, interval, rule, date and time inputs.
 * @returns The schedule fields, or null when a calendar rule is invalid.
 */
function resolveScheduleInputs(
   inputs: ScheduleInputs,
): Pick<Habit, 'kind' | 'scheduleDays' | 'dueDate' | 'scheduleRule'> | null {
   if (inputs.kind === 'calendar') {
      const scheduleRule = normalizeScheduleRule(inputs.scheduleRule)
      if (!scheduleRule) {
         return null
      }
      return {
         kind: 'calendar',
         scheduleDays: approximateScheduleDays(scheduleRule),
         dueDate: parseLocalDateTimeInput(inputs.dueDate, inputs.dueTime) ?? inputs.now,
         scheduleRule,
      }
   }
   const { kind } = inputs
   return {
      kind,
      scheduleDays: normalizeScheduleDays(inputs.scheduleDays),
      dueDate:
         kind === 'one-off' ?
            (parseLocalDateTimeInput(inputs.dueDate, inputs.dueTime) ??
            getLocalDayStart(inputs.now))
         :  null,
      scheduleRule: null,
   }
}

type HabitDetailsInput = Omit<ScheduleInputs, 'now'> & {
   name: string
   alsoCompletes?: string[]
   tags?: string[]
}

/**
 * Updates a habit's editable fields.
 *
 * @param habitId - The habit id.
 * @param details - Name, kind, schedule (days or RRULE), due date/time, linked habits, and tags.
 * @returns True when updated, false when the name is empty or the rule is invalid.
 */
export function updateHabitDetails(habitId: string, details: HabitDetailsInput) {
   const normalizedName = details.name.trim()
   if (!normalizedName) {
      return false
   }

   const now = Date.now()
   const schedule = resolveScheduleInputs({ ...details, now })
   if (!schedule) {
      return false
   }
   const { kind } = schedule
   const alsoCompletesInput = details.alsoCompletes ?? []
   commitHabits(
      habits().map(habit =>
         habit.id === habitId ?
            {
               ...habit,
               name: normalizedName,
               ...schedule,
               alsoCompletes:
                  kind === 'recurring' ?
                     normalizeAlsoCompletes(alsoCompletesInput, habitId)
                  :  [],
               tags: details.tags ? normalizeHabitTags(details.tags) : habit.tags,
               updatedAt: now,
            }
         :  habit,
      ),
      'update-habit',
   )
   return true
}

/**
 * Deletes a habit and records a deletion tombstone.
 *
 * @param habitId - The habit id.
 */
export function deleteHabit(habitId: string) {
   deletedHabitTombstones.set(habitId, Date.now())
   persistDeletedHabitTombstones()
   commitHabits(
      habits().filter(habit => habit.id !== habitId),
      'delete-habit',
   )
}

/**
 * Returns a habit with its paused flag set and its pause period opened or closed.
 *
 * @param habit - The habit.
 * @param paused - The new paused state.
 * @param now - When the change happened.
 * @returns The updated habit.
 */
function withPausedState(habit: Habit, paused: boolean, now: number): Habit {
   return {
      ...habit,
      paused,
      pausePeriods: applyPausedState(habit.pausePeriods, paused, now),
      updatedAt: now,
   }
}

/**
 * Toggles a habit's paused state.
 *
 * @param habitId - The habit id.
 */
export function toggleHabitPaused(habitId: string) {
   const now = Date.now()
   commitHabits(
      habits().map(habit =>
         habit.id === habitId ? withPausedState(habit, !habit.paused, now) : habit,
      ),
      'toggle-habit-paused',
   )
}

/**
 * Pauses or resumes every habit carrying a tag, whatever state each was in:
 * the latest action wins, also for habits that share other tags.
 *
 * @param tag - The tag (matched ignoring case).
 * @param paused - The new paused state.
 */
export function setHabitsPausedByTag(tag: string, paused: boolean) {
   const now = Date.now()
   commitHabits(
      habits().map(habit =>
         habitHasTag(habit.tags, tag) && habit.paused !== paused ?
            withPausedState(habit, paused, now)
         :  habit,
      ),
      'set-tag-paused',
   )
}

/** A tag with how many habits carry it and how many of those are paused. */
export type HabitTagSummary = { tag: string; total: number; paused: number }

/**
 * Lists the tags in use, alphabetically, with habit and paused counts. The
 * first spelling seen names a tag that differs only in case.
 *
 * @param currentHabits - The habits to scan.
 * @returns The tag summaries.
 */
export function getHabitTagSummaries(currentHabits: readonly Habit[]): HabitTagSummary[] {
   const byKey = new Map<string, HabitTagSummary>()
   for (const habit of currentHabits) {
      for (const tag of habit.tags) {
         const key = tag.toLowerCase()
         const summary = byKey.get(key) ?? { tag, total: 0, paused: 0 }
         summary.total += 1
         summary.paused += habit.paused ? 1 : 0
         byKey.set(key, summary)
      }
   }
   return [...byKey.values()].sort((a, b) =>
      a.tag.localeCompare(b.tag, undefined, { sensitivity: 'base' }),
   )
}

/**
 * Starts the completion animation for one or more habit cards.
 *
 * @param habitIds - The habit ids to animate.
 */
function startHabitCelebration(habitIds: string[]) {
   const uniqueHabitIds = Array.from(new Set(habitIds))
   setCelebratingHabitIds(current => Array.from(new Set([...current, ...uniqueHabitIds])))
   window.setTimeout(() => {
      setCelebratingHabitIds(current =>
         current.filter(habitId => !uniqueHabitIds.includes(habitId)),
      )
   }, HABIT_COMPLETION_ANIMATION_MS)
}

/**
 * Marks a habit complete (removing one-off habits) and triggers the celebration animation.
 *
 * Recurring habits reachable through `alsoCompletes` links (transitively) are
 * stamped with the same completion timestamp, so a harder habit can satisfy
 * easier ones.
 *
 * @param habitId - The habit id.
 */
export function completeHabit(habitId: string) {
   const now = Date.now()
   const completedHabit = habits().find(habit => habit.id === habitId)
   if (completedHabit?.kind === 'one-off') {
      deletedHabitTombstones.set(habitId, now)
      persistDeletedHabitTombstones()
      commitHabits(
         habits().filter(habit => habit.id !== habitId),
         'delete-habit',
      )
      startHabitCelebration([habitId])
      return
   }

   const completedIds = resolveHabitCompletionChain(habitId, habits())
   const completedIdSet = new Set(completedIds)
   commitHabits(
      habits().map(habit => {
         if (!completedIdSet.has(habit.id)) {
            return habit
         }
         return {
            ...habit,
            lastCompletedAt: now,
            completionHistory: [...habit.completionHistory, now],
            updatedAt: now,
         }
      }),
      'complete-habit',
   )
   startHabitCelebration(completedIds)
}

/**
 * Computes when a habit is next due, based on kind and last completion.
 *
 * @param habit - The habit.
 * @returns The next-due timestamp in epoch ms.
 */
export function getHabitNextDueAt(habit: Habit, now = Date.now()) {
   if (habit.kind === 'one-off') {
      return habit.dueDate ?? habit.createdAt
   }
   if (habit.kind === 'calendar') {
      // Infinity once a finite rule has no more occurrences: never due again.
      return habit.scheduleRule ?
            (getCalendarNextDueAt(
               {
                  scheduleRule: habit.scheduleRule,
                  anchorAt: habit.dueDate ?? habit.createdAt,
                  completionHistory: habit.completionHistory,
                  lastCompletedAt: habit.lastCompletedAt,
               },
               now,
            ) ?? Number.POSITIVE_INFINITY)
         :  Number.POSITIVE_INFINITY
   }
   if (habit.lastCompletedAt === null) {
      return habit.createdAt
   }
   return getLocalDayStartAfterDays(habit.lastCompletedAt, habit.scheduleDays)
}

/**
 * Returns whether a habit is due at a given time.
 *
 * @param habit - The habit.
 * @param now - The reference time (defaults to now).
 * @returns True when due.
 */
export function isHabitDue(habit: Habit, now = Date.now()) {
   // Due for the whole calendar day: a task at 09:00 today is today's task already at 04:00.
   return getHabitNextDueAt(habit, now) < getLocalDayStartAfterDays(now, 1)
}

/**
 * Returns whether an unpaused recurring habit has gone undone for at least twice
 * its schedule interval (counted in calendar days from the last completion, or
 * from creation when it was never completed).
 *
 * @param habit - The habit.
 * @param now - The reference time (defaults to now).
 * @returns True when the habit is overdue by a full extra interval.
 */
export function isHabitOverdue(habit: Habit, now = Date.now()) {
   if (habit.kind === 'one-off' || habit.paused) {
      return false
   }
   if (habit.kind === 'calendar') {
      return (
         habit.scheduleRule !== null &&
         isCalendarHabitOverdue(
            {
               scheduleRule: habit.scheduleRule,
               anchorAt: habit.dueDate ?? habit.createdAt,
               completionHistory: habit.completionHistory,
               lastCompletedAt: habit.lastCompletedAt,
            },
            now,
         )
      )
   }
   // A resumed habit gets a fresh interval before it counts as overdue.
   const anchor = Math.max(
      habit.lastCompletedAt ?? habit.createdAt,
      getHabitLastResumedAt(habit) ?? Number.NEGATIVE_INFINITY,
   )
   return getLocalDayStartAfterDays(anchor, habit.scheduleDays * 2) <= now
}

/**
 * Counts unpaused habits that are due at a given time (one-off tasks once their date
 * arrives); used for the app badge.
 *
 * @param currentHabits - The habits to consider.
 * @param now - The reference time (defaults to now).
 * @returns The number of open habit tasks.
 */
export function getOpenHabitTaskCount(currentHabits: Habit[], now = Date.now()) {
   return currentHabits.filter(habit => !habit.paused && isHabitDue(habit, now)).length
}

/**
 * Returns a translation key and params describing when a habit is due, counted in
 * calendar days (a task at 09:00 tomorrow is "due tomorrow" even 5 hours before).
 *
 * @param habit - The habit.
 * @param now - The reference time (defaults to now).
 * @returns A label key, day count, and (for later today) the due time.
 */
export function getHabitDueLabel(habit: Habit, now = Date.now()) {
   const dueAt = getHabitNextDueAt(habit, now)
   if (!Number.isFinite(dueAt)) {
      return { key: 'No upcoming dates', count: 0 }
   }
   const diffDays = Math.round((getLocalDayStart(dueAt) - getLocalDayStart(now)) / DAY_MS)
   if (diffDays === 0 && dueAt > now) {
      return { key: 'Due at {time}', count: 0, time: getLocalTimeInputValue(dueAt) }
   }
   if (diffDays <= 0) {
      return { key: 'Due now', count: 0 }
   }
   if (diffDays === 1) {
      return { key: 'Due tomorrow', count: 1 }
   }
   return { key: 'Due in {count} days', count: diffDays }
}

/**
 * Returns a translation key and count describing when a habit was last completed.
 *
 * Same-day completions are described in hours; once local midnight passes, the
 * label counts calendar days so it matches the calendar-based due date.
 *
 * @param habit - The habit.
 * @param now - The reference time (defaults to now).
 * @returns A label key and count.
 */
export function getHabitCompletionLabel(habit: Habit, now = Date.now()) {
   if (habit.lastCompletedAt === null) {
      return { key: 'Not completed yet', count: 0 }
   }

   const dayDiff = Math.round(
      (getLocalDayStart(now) - getLocalDayStart(habit.lastCompletedAt)) / DAY_MS,
   )
   if (dayDiff === 1) {
      return { key: 'Completed yesterday', count: 1 }
   }
   if (dayDiff > 1) {
      return { key: 'Completed {count} days ago', count: dayDiff }
   }

   const elapsedHours = Math.floor(Math.max(0, now - habit.lastCompletedAt) / HOUR_MS)
   if (elapsedHours < 1) {
      return { key: 'Completed less than 1 hour ago', count: 0 }
   }
   if (elapsedHours === 1) {
      return { key: 'Completed 1 hour ago', count: 1 }
   }
   return { key: 'Completed {count} hours ago', count: elapsedHours }
}

/**
 * Returns a translation key and count describing a habit's schedule.
 *
 * @param habit - The habit.
 * @returns A label key and count.
 */
export function getHabitScheduleLabel(habit: Habit) {
   if (habit.kind === 'one-off') {
      return { key: 'One-off', count: 0 }
   }
   if (habit.kind === 'calendar') {
      const preset = SCHEDULE_RULE_PRESETS.find(item => item.rule === habit.scheduleRule)
      return { key: preset?.label ?? 'Custom schedule', count: 0 }
   }
   return { key: 'Every {count} days', count: habit.scheduleDays }
}

/** The calendar repeat choices offered in the habit form, as RRULEs. */
export const SCHEDULE_RULE_PRESETS = [
   { rule: 'FREQ=DAILY', label: 'Every day' },
   { rule: 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR', label: 'Every weekday' },
   { rule: 'FREQ=WEEKLY', label: 'Every week' },
   { rule: 'FREQ=WEEKLY;INTERVAL=2', label: 'Every 2 weeks' },
   { rule: 'FREQ=MONTHLY', label: 'Every month' },
   { rule: 'FREQ=YEARLY', label: 'Every year' },
] as const

/**
 * Returns the local `YYYY-MM-DD` key for a timestamp.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @returns The day key.
 */
export function getHabitDayKey(timestamp: number) {
   return formatLocalDateKey(timestamp)
}

/**
 * Returns the local-midnight timestamp for a given time.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @returns Epoch ms at local midnight.
 */
export function getLocalDayStart(timestamp: number) {
   const date = new Date(timestamp)
   date.setHours(0, 0, 0, 0)
   return date.getTime()
}

/**
 * Returns the local-midnight timestamp N days after a given time.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @param days - Days to add.
 * @returns Epoch ms at local midnight of the target day.
 */
export function getLocalDayStartAfterDays(timestamp: number, days: number) {
   const date = new Date(timestamp)
   date.setHours(0, 0, 0, 0)
   date.setDate(date.getDate() + days)
   return date.getTime()
}

export {
   celebratingHabitIds,
   habitAlsoCompletesInput,
   habitDueDateInput,
   habitDueTimeInput,
   habitKindInput,
   habitNameInput,
   habitScheduleDaysInput,
   habitScheduleRuleInput,
   habitTagsInput,
   habits,
   setHabitAlsoCompletesInput,
   setHabitDueDateInput,
   setHabitDueTimeInput,
   setHabitKindInput,
   setHabitNameInput,
   setHabitScheduleDaysInput,
   setHabitScheduleRuleInput,
   setHabitTagsInput,
}
